import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "@/hooks/LoaderProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { PlusOutlined } from "@/config/antd";
import {
  fetchEventByAny,
  selectAllEvents,
  selectEventLoading,
  selectEventTotal,
  selectFetchedEvents,
  deleteEvent,
} from "@/store/redux/slices/event/event_slice";
import { getOrganizationId } from "@/utils/token_functions";
import { isScheduleExpired } from "@/utils/calendar_utils";
import PageHeader from "@/components/layout/Header/Page_Header";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import Breadcrumb from "@/components/specific/Breadcrumb";
import EventHomeTable from "@/pages/event_management/event_home/EventHomeTable";
import TrashIcon from "@/assets/icons/web_Icons/trash_red.svg";
import EmptyState from "@/pages/event_management/single_campaign/Empty_State";
import EventCard from "@/pages/event_management/event_home/Event_Card";
import { calculateEventCompletionPercentage } from "@/utils/event_utils";

const EventHome = () => {
  const dispatch = useDispatch();
  const events = useSelector(selectAllEvents);
  const fetchedEventsByOrg = useSelector(selectFetchedEvents);
  const totalEvents = useSelector(selectEventTotal);
  const isLoading = useSelector(selectEventLoading);
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("Today");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [deletedEventTitle, setDeletedEventTitle] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  // Use a helper to determine which data source to use
  const getEventsToUse = useCallback(() => {
    return fetchedEventsByOrg?.length > 0 ? fetchedEventsByOrg : events;
  }, [fetchedEventsByOrg, events]);

  // Memoize fetchEventData with proper dependencies
  const fetchEventData = useCallback(async () => {
    const organizationID = getOrganizationId();

    showLoader();
    try {
      await dispatch(
        fetchEventByAny({
          key: "organizationID",
          value: organizationID,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      hideLoader();
    }
  }, [dispatch, showLoader, hideLoader]);

  
  useEffect(() => {
    fetchEventData();
  }, [fetchEventData]);

  useEffect(() => {
    // Handle URL parameters without triggering fetching
    const searchString =
      location.search ||
      (location.hash && location.hash.includes("?")
        ? location.hash.substring(location.hash.indexOf("?"))
        : "");

    const searchParams = new URLSearchParams(searchString);
    const dateParam = searchParams.get("date");

    if (dateParam) {
      // Set active filter to date
      setActiveFilter("date");

      setSearchValue(dateParam);

      // Format date for display
      try {
        const dateObj = new Date(dateParam);
        const displayDate = dateObj.toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

        // Set filter value for display
        setFilterValue(`Events on ${displayDate}`);
      } catch (error) {
        console.error("Error formatting date:", error);
      }
    }
  }, [location.search, location.hash]);

  // Extract dates from events that have events
  const getEventDates = useCallback(() => {
    if (!getEventsToUse() || getEventsToUse().length === 0) return [];

    const dates = getEventsToUse()
      .map((event) => {
        // Use start date of the event
        return event.schedule?.startDate
          ? new Date(event.schedule.startDate)
          : null;
      })
      .filter(Boolean); // Remove any null values

    return dates;
  }, [getEventsToUse]);

  // Transform data for cards (mobile view)
  const getFilteredCardsData = useCallback(() => {
    let filtered = getEventsToUse();
    const currentDate = new Date();

    if (!filtered || filtered?.length === 0) return [];

    // Apply search filter if searchValue exists
    if (searchValue.trim()) {
      if (activeFilter === "date") {
        // Use EXACTLY the same filtering logic as the HomeCalender component
        filtered = filtered.filter((event) => {
          // Check if the event's start date matches this date
          const eventStartDate = event?.schedule?.startDate;
          if (!eventStartDate) return false;

          // Convert event date to YYYY-MM-DD format for comparison
          const eventDate = new Date(eventStartDate);
          const eventFormattedDate = eventDate.toISOString().split("T")[0];

          // Compare with the searchValue (which should be YYYY-MM-DD format)
          return eventFormattedDate === searchValue;
        });
      } else {
        // Filter by name
        filtered = filtered.filter((event) =>
          event.name.toLowerCase().includes(searchValue.toLowerCase())
        );
      }
    }

    // Apply status filter (local end-of-day + endTime, same as SR)
    switch (activeFilter) {
      case "active":
        filtered = filtered.filter(
          (event) => !isScheduleExpired(event.schedule, currentDate)
        );
        break;
      case "expired":
        filtered = filtered.filter((event) =>
          isScheduleExpired(event.schedule, currentDate)
        );
        break;
      default:
        break;
    }

    // Transform for cards
    return filtered.map((event) => ({
      id: event.id || event._id,
      _id: event._id,
      title: event.name,
      progress: calculateEventCompletionPercentage(event),
      publishDate: new Date(event.createdAt).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      }),
      status: event.status,
      endDate: event.schedule?.endDate,
      endTime: event.schedule?.endTime,
      startDate: event.schedule?.startDate,
    }));
  }, [getEventsToUse, searchValue, activeFilter]);

  // Use an effect to update the filter value safely
  useEffect(() => {
    if (activeFilter === "date" && searchValue) {
      // Check if there are any events on this date
      const eventsOnDate = getEventsToUse().filter((event) => {
        if (!event.schedule?.startDate) return false;
        const eventDateString = new Date(event.schedule.startDate)
          .toISOString()
          .split("T")[0];
        return eventDateString === searchValue;
      });

      if (eventsOnDate.length === 0) {
        // No events on this exact date
        setFilterValue(
          `Events near ${new Date(searchValue).toLocaleDateString()}`
        );
      } else {
        // Events found on this date
        setFilterValue(
          `Events on ${new Date(searchValue).toLocaleDateString()}`
        );
      }
    }
  }, [activeFilter, searchValue, getEventsToUse]);

  // Transform data for table (desktop view)
  const getFilteredTableData = useCallback(() => {
    let filtered = getEventsToUse();
    const currentDate = new Date();

    if (!filtered || filtered?.length === 0) return [];

    // Apply search filter if searchValue exists
    if (searchValue.trim()) {
      if (activeFilter === "date") {
        filtered = filtered.filter((event) => {
          // Check if the event's start date matches this date
          const eventStartDate = event?.schedule?.startDate;
          if (!eventStartDate) return false;

          // Convert event date to YYYY-MM-DD format for comparison
          const eventDate = new Date(eventStartDate);
          const eventFormattedDate = eventDate.toISOString().split("T")[0];

          // Compare with the searchValue (which should be YYYY-MM-DD format)
          return eventFormattedDate === searchValue;
        });
      } else {
        // Filter by name
        filtered = filtered.filter((event) =>
          event.name.toLowerCase().includes(searchValue.toLowerCase())
        );
      }
    }

    // Apply status filter (local end-of-day + endTime, same as SR)
    switch (activeFilter) {
      case "active":
        filtered = filtered.filter(
          (event) => !isScheduleExpired(event.schedule, currentDate)
        );
        break;
      case "expired":
        filtered = filtered.filter((event) =>
          isScheduleExpired(event.schedule, currentDate)
        );
        break;
      default:
        break;
    }

    return filtered; 
  }, [getEventsToUse, searchValue, activeFilter]);

  // Calculate counts for filters
  const getEventCounts = useCallback(() => {
    const eventsToCount = getEventsToUse();
    if (!eventsToCount || eventsToCount?.length === 0)
      return { total: 0, active: 0, expired: 0 };

    const currentDate = new Date();

    const counts = eventsToCount.reduce(
      (acc, event) => {
        if (isScheduleExpired(event.schedule, currentDate)) {
          acc.expired += 1;
        } else {
          acc.active += 1;
        }

        return acc;
      },
      { total: eventsToCount.length, active: 0, expired: 0 }
    );

    return counts;
  }, [getEventsToUse]);

  // Get the data for rendering
  const cardData = getFilteredCardsData();
  const tableData = getFilteredTableData();
  const eventCounts = getEventCounts();

  const handleModify = useCallback(
    (eventId) => {
      navigate(`/dashboard/events/edit/${eventId}`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    async (eventId, eventTitle) => {
      showLoader();
      try {
        await dispatch(deleteEvent(eventId)).unwrap();
        setDeletedEventTitle(eventTitle);
        await fetchEventData();
        setIsSuccessVisible(true);
      } catch (error) {
        console.error("Delete failed:", error);
      } finally {
        hideLoader();
      }
    },
    [showLoader, hideLoader, dispatch, fetchEventData]
  );

  const handleAddNewClick = () => {
    if (selectedEvents?.length > 0) {
      // Show delete confirmation dialog
      setIsDeleteModalVisible(true);
    } else {
      // Navigate to add new event page
      navigate("/dashboard/events/add");
    }
  };

  // Funtion to delete multiple events
  const handleBulkDelete = async () => {
    if (selectedEvents?.length === 0) return;
    setIsDeleteModalVisible(false);
    showLoader();
    try {
      const eventsToDelete = getEventsToUse()?.filter((event) =>
        selectedEvents?.includes(event._id)
      );
      const eventCount = eventsToDelete?.length;

      // If only one event, use its title, otherwise use count
      if (eventCount === 1) {
        setDeletedEventTitle(eventsToDelete[0]?.name);
      } else {
        setDeletedEventTitle(`${eventCount} events`);
      }

      // Delete all selected events
      await Promise.all(
        selectedEvents?.map((eventId) =>
          dispatch(deleteEvent(eventId)).unwrap()
        )
      );

      // Refresh data and reset selection
      await fetchEventData();
      setSelectedEvents([]);
      setIsDeleteModalVisible(false);
      setIsSuccessVisible(true);
    } catch (error) {
      console.error("Bulk delete failed:", error);
    } finally {
      hideLoader();
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    // Implement search logic here
  };

  const handleRefresh = useCallback(() => {
    // 1. Reset pagination to first page when refreshing
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all' to show all data
    setActiveFilter("all");

    // 4. Reset the display text for the filter button
    setFilterValue("Today");

    // 5. Fetch fresh data
    fetchEventData();
  }, [fetchEventData]);

  // Add a handler for the filter change from SearchFilterBar
  const handleFilterChange = (displayValue, dateValue) => {
    // Update display filter value
    setFilterValue(displayValue);

    if (dateValue) {
      setActiveFilter("date");
      setSearchValue(dateValue);
    } else {
      // Handle other filter types if needed
      setActiveFilter("all");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const filterOptions = {
    items: [
      { key: "today", label: "Today" },
      { key: "thisWeek", label: "This Week" },
      { key: "thisMonth", label: "This Month" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  const moreOptionsMenu = {
    items: [
      { key: "1", label: "Date Created" },
      { key: "2", label: "Open Calendar" },
    ],
  };

  // Define breadcrumb items
  const breadcrumbItems = [{ title: "Events", link: "/dashboard/events" }];

  // Handler for when a user clicks on a date with events
  const handleMarkedDateClick = (date) => {
    // Format date to YYYY-MM-DD for filtering
    const formattedDate = date.toISOString().split("T")[0];

    // Set active filter
    setActiveFilter("date");

    // Format date for display
    const displayDate = date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    // Set filter value for display
    setFilterValue(displayDate);

    // Set search value for filtering
    setSearchValue(formattedDate);

  };

  // Return null if loading to show the global loader
  if (isLoading && getEventsToUse()?.length === 0) {
    return null;
  }

  return (
    <div className="text-left" data-testid="event-home-container">
      <div>
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="flex flex-start flex-col gap-4">
        <div>
          <PageHeader title="Events" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div className="flex flex-col text-left">
              <h1 className="heading-medium text-darkGrey">
                {totalEvents} Total Events
              </h1>
              <h1 className="text-regular text-darkGrey">
                Total number of Events created
              </h1>
            </div>
            <div className="items-center hidden gap-2 lg:flex">
              <Button
                text={`All campaigns (${totalEvents})`}
                color="primary"
                className="px-2 py-1 text-sm sm:px-3 sm:py-2 sm:text-small min-w-[80px] sm:min-w-[100px]"
                iconPosition="left"
                customBgColor={activeFilter === "all" ? "primary" : "white"}
                customTextColor={activeFilter === "all" ? "white" : "primary"}
                onClick={() => setActiveFilter("all")}
              />
              <Button
                text={`Active (${eventCounts.active})`}
                className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
                customBgColor={activeFilter === "active" ? "primary" : "white"}
                customTextColor={
                  activeFilter === "active" ? "white" : "primary"
                }
                onClick={() => setActiveFilter("active")}
              />
              <Button
                text={`Expired (${eventCounts.expired})`}
                className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
                customBgColor={activeFilter === "expired" ? "primary" : "white"}
                customTextColor={
                  activeFilter === "expired" ? "white" : "primary"
                }
                onClick={() => setActiveFilter("expired")}
              />
              {activeFilter === "date" && (
                <Button
                  text={`Events on ${new Date(
                    searchValue
                  ).toLocaleDateString()}`}
                  className="border border-primary font-normal text-sm !px-3 !py-2 h-8"
                  customBgColor="primary"
                  customTextColor="white"
                  onClick={() => {}} // Already filtered
                />
              )}
            </div>
          </div>
          <Button
            data-testid="add-new-event-btn"
            text={selectedEvents?.length !== 0 ? "DELETE SELECTED" : "ADD NEW"}
            icon={
              selectedEvents?.length !== 0 ? (
                <img src={TrashIcon} />
              ) : (
                <PlusOutlined />
              )
            }
            color={selectedEvents?.length !== 0 ? "outline" : "primary"}
            className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] whitespace-nowrap sm:min-w-[100px]"
            iconPosition="left"
            customBgColor={selectedEvents?.length !== 0 ? "" : "primary"}
            customTextColor={selectedEvents?.length !== 0 ? "red" : "white"}
            onClick={handleAddNewClick}
          />
        </div>
      </div>

      <div className="mt-4">
        <SearchFilterBar
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onFilterChange={handleFilterChange}
          filterValue={filterValue}
          filterOptions={filterOptions}
          moreOptionsMenu={moreOptionsMenu}
          placeholder="Search events..."
          data-testid="event-search-filter"
          markedDates={getEventDates()}
          onMarkedDateClick={handleMarkedDateClick}
          searchValue={searchValue}
        />
      </div>

      {/* Conditional Rendering for Empty State */}
      {getEventsToUse()?.length === 0 ? (
        <EmptyState
          title="Events Empty"
          description="There's nothing to show here."
          data-testid="empty-state"
        />
      ) : cardData?.length === 0 && tableData?.length === 0 ? (
        <EmptyState
          title="No Matching Events"
          description="No events match your current filters."
          data-testid="empty-state"
        />
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="grid grid-cols-1 gap-4 mt-6 mb-6 md:grid-cols-2 lg:grid-cols-1 lg:hidden">
            {cardData?.map((event) => (
              <EventCard
                data-testid={`event-card-${event.id}`}
                key={event.id}
                event={event}
                onDelete={handleDelete}
                onModify={handleModify}
              />
            ))}
          </div>

          {/* Desktop Table View */}
          {tableData?.length > 0 && (
            <div
              data-testid="event-table-container"
              className="hidden lg:block">
              <EventHomeTable
                setSelectedEvents={setSelectedEvents}
                currentPage={currentPage}
                pageSize={pageSize}
                total={totalEvents}
                onDelete={handleDelete}
                onModify={handleModify}
                filteredEvents={tableData}
                activeFilter={activeFilter}
              />
            </div>
          )}

          {/* Pagination */}
          <CustomPagination
            current={currentPage}
            total={totalEvents}
            pageSize={pageSize}
            onChange={handlePageChange}
            className="pb-8"
            showTotal={true}
          />
        </>
      )}

      <Success
        isVisible={isSuccessVisible}
        onClose={() => setIsSuccessVisible(false)}
        header="Event Deleted!"
        paragraph={`You have successfully deleted the event "${deletedEventTitle}" from your list of ongoing events.`}
        buttonText="Close"
        onButtonClick={() => setIsSuccessVisible(false)}
      />

      {/* Add Delete confirmation modal */}
      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleBulkDelete}
        header="Delete Selected Events?"
        paragraph={`Are you sure you want to delete ${selectedEvents?.length} selected events? This action cannot be undone.`}
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </div>
  );
};

export { EventCard, EventHomeTable };
export default EventHome;
